// Media utility functions for handling video and image URLs

/**
 * Check if a URL is a YouTube video
 * Supports various YouTube URL formats:
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://www.youtube.com/embed/VIDEO_ID
 */
export const isYouTubeURL = (url) => {
  if (!url || typeof url !== 'string') return false;
  
  const youtubeRegex = /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)/i;
  return youtubeRegex.test(url);
};

/**
 * Check if a URL is an image
 * First checks for common image extensions, then assumes non-YouTube URLs are images
 */
export const isImageURL = (url) => {
  if (!url || typeof url !== 'string') return false;
  
  // If it's a YouTube URL, it's definitely not an image
  if (isYouTubeURL(url)) return false;
  
  // Check for common image extensions
  const imageExtensionRegex = /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)(\?.*)?$/i;
  if (imageExtensionRegex.test(url)) return true;
  
  // For URLs without extensions, assume they're images if they're HTTP(S) URLs
  // This handles cases like CDN URLs or dynamic image URLs
  const httpRegex = /^https?:\/\//i;
  return httpRegex.test(url);
};

/**
 * Get YouTube video thumbnail URL
 * Extracts video ID and returns thumbnail URL
 */
export const getYouTubeThumbnail = (url) => {
  if (!isYouTubeURL(url)) return null;
  
  let videoId = null;
  
  // Extract video ID from different YouTube URL formats
  if (url.includes('watch?v=')) {
    videoId = url.split('watch?v=')[1]?.split('&')[0];
  } else if (url.includes('youtu.be/')) {
    videoId = url.split('youtu.be/')[1]?.split('?')[0];
  } else if (url.includes('embed/')) {
    videoId = url.split('embed/')[1]?.split('?')[0];
  }
  
  return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
};

/**
 * Convert YouTube watch URL to embed URL
 */
export const getYouTubeEmbedURL = (url) => {
  if (!isYouTubeURL(url)) return url;
  
  let videoId = null;
  
  if (url.includes('watch?v=')) {
    videoId = url.split('watch?v=')[1]?.split('&')[0];
  } else if (url.includes('youtu.be/')) {
    videoId = url.split('youtu.be/')[1]?.split('?')[0];
  } else if (url.includes('embed/')) {
    return url; // Already an embed URL
  }
  
  return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
};

/**
 * Get media type for display purposes
 */
export const getMediaType = (url) => {
  if (isYouTubeURL(url)) return 'video';
  if (isImageURL(url)) return 'image';
  return 'unknown';
};